// Dock flutuante de mensagens não-lidas — estilo LinkedIn. Aparece no canto
// inferior esquerdo (esquerda porque o canto inferior direito já está
// ocupado pelo PersistentPlayer/PeerChatDock). Mostra até N mensagens
// não-lidas mais recentes; clique em qualquer item navega pra view de
// mensagens. Opt-in via Preferências (`prefs.mensagensDock`).
//
// Regras:
//   - Só renderiza no desktop (≥ md). Em telas pequenas, a sidebar resolve.
//   - Some quando `unreadCount === 0`.
//   - Aluno pode minimizar (chevron) — fica como pílula compacta com badge.
//   - Aluno pode fechar dispensando até a próxima mensagem nova chegar
//     (controle local da sessão; não persistido).
//   - Click em mensagem: marca como lida + abre a view Mensagens.
//
// [[project-unread-messages-indicator]] [[project-peer-chat-floating-and-hub]]

import { useEffect, useState } from "preact/hooks";
import { ChevronDown, ChevronUp, Mail, X } from "lucide-react";
import { inboxState, setMessageRead } from "./MensagensView";
import { setActiveNav, prefs } from "./App";

const MAX_PREVIEW = 5;

export function InboxDock() {
  const mensagensDockEnabled = prefs.value.mensagensDock;
  const state = inboxState.value;
  const unread = state.list.filter((m) => !m.readAt);

  const [minimized, setMinimized] = useState(false);
  const [dismissedAt, setDismissedAt] = useState<number | null>(null);

  // Se chegou mensagem nova depois do dismiss, mostra de novo.
  const newest = unread[0];
  const newestTs = newest ? Date.parse(newest.createdAt) : 0;
  useEffect(() => {
    if (dismissedAt != null && newestTs > dismissedAt) {
      setDismissedAt(null);
    }
  }, [newestTs]);

  if (!mensagensDockEnabled) return null;
  if (unread.length === 0) return null;
  if (dismissedAt != null) return null;

  const preview = unread.slice(0, MAX_PREVIEW);
  const extra = unread.length - preview.length;

  function openInboxAt(id: string) {
    void setMessageRead(id, true);
    setActiveNav("mensagens");
  }

  function openAll() {
    setActiveNav("mensagens");
  }

  function dismiss() {
    setDismissedAt(Date.now());
  }

  // Pílula minimizada — só ícone + count.
  if (minimized) {
    return (
      <button
        type="button"
        onClick={() => setMinimized(false)}
        title={`${unread.length} mensagem${unread.length === 1 ? "" : "s"} não-lida${
          unread.length === 1 ? "" : "s"
        }`}
        class="fixed bottom-4 left-4 z-40 hidden items-center gap-2 rounded-full border border-amber-500/40 bg-card px-3 py-2 text-xs font-bold text-amber-600 shadow-lg hover:bg-amber-500/10 dark:text-amber-400 md:inline-flex"
      >
        <Mail className="size-4" />
        <span class="tabular-nums">
          {unread.length} não-lida{unread.length === 1 ? "" : "s"}
        </span>
        <ChevronUp className="size-3" />
      </button>
    );
  }

  return (
    <aside
      class="fixed bottom-4 left-4 z-40 hidden w-[min(360px,calc(100vw-2rem))] flex-col overflow-hidden rounded-md border border-border bg-card shadow-2xl md:flex"
      role="region"
      aria-label="Mensagens não-lidas"
    >
      <header class="flex items-center gap-2 border-b border-border bg-amber-500/5 px-3 py-2">
        <Mail className="size-4 text-amber-600 dark:text-amber-400" />
        <h2 class="text-sm font-bold">
          Mensagens · <span class="text-amber-600 tabular-nums dark:text-amber-400">{unread.length}</span>
        </h2>
        <div class="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={() => setMinimized(true)}
            title="Minimizar"
            aria-label="Minimizar"
            class="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <ChevronDown className="size-4" />
          </button>
          <button
            type="button"
            onClick={dismiss}
            title="Fechar até nova mensagem"
            aria-label="Fechar"
            class="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </div>
      </header>
      <ul class="max-h-[50vh] divide-y divide-border overflow-y-auto">
        {preview.map((m) => (
          <li key={m.id}>
            <button
              type="button"
              onClick={() => openInboxAt(m.id)}
              class="block w-full px-3 py-2.5 text-left transition-colors hover:bg-accent/40"
            >
              <div class="flex items-start gap-2">
                <span class="mt-1 size-2 shrink-0 rounded-full bg-amber-500" aria-hidden />
                <div class="min-w-0 flex-1">
                  <div class="flex items-baseline justify-between gap-2">
                    <span class="truncate text-xs font-bold">{m.fromName}</span>
                    <span class="shrink-0 text-[10px] text-muted-foreground tabular-nums">
                      {formatWhen(m.createdAt)}
                    </span>
                  </div>
                  <div class="truncate text-[12px] font-semibold text-foreground">
                    {m.title}
                  </div>
                  <p class="mt-0.5 line-clamp-2 text-[11px] text-muted-foreground">
                    {stripMarkdown(m.body)}
                  </p>
                </div>
              </div>
            </button>
          </li>
        ))}
      </ul>
      <footer class="flex items-center justify-between gap-2 border-t border-border bg-card/80 px-3 py-2">
        <span class="text-[10px] text-muted-foreground">
          {extra > 0 ? `+${extra} mais não-lida${extra === 1 ? "" : "s"}` : "Tudo aqui"}
        </span>
        <button
          type="button"
          onClick={openAll}
          class="text-xs font-bold text-amber-600 hover:underline dark:text-amber-400"
        >
          Ver todas →
        </button>
      </footer>
    </aside>
  );
}

function formatWhen(iso: string): string {
  const ms = Date.now() - Date.parse(iso);
  const min = Math.round(ms / 60_000);
  if (min < 1) return "agora";
  if (min < 60) return `${min}min`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h}h`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d`;
  return new Date(iso).toLocaleDateString("pt-BR");
}

function stripMarkdown(s: string): string {
  // Remove os marcadores mais comuns para a prévia ficar limpa.
  return s
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/==([^=]+)==/g, "$1")
    .replace(/\+\+([^+]+)\+\+/g, "$1")
    .replace(/\{\{color:[a-z]+\}\}([\s\S]*?)\{\{\/color\}\}/gi, "$1")
    .replace(/\n+/g, " ")
    .trim();
}
